/**
 * Per-engine state of the notes module (W1-06). Per-line runtimes live in `ctx.notesStore` (LineId -> LineNotesRt);
 * everything global (multi-highlight map, note index, sorted real-note index, emission cache, frame scratch) is
 * kept in a WeakMap keyed by that store, so it lives exactly as long as the engine.
 *
 * Self-healing freshness: an access checks a runtime against the current context (IR line identity, tempo map and
 * tempo epoch, maxTime, speed mode, RPE version) and rebuilds it when stale. Chart-wide queries and the frame passes
 * check every line (syncAll, O(lines)); per-note queries check only the note's own line, so they stay O(1) and rely
 * on the engine's compile calls (or the next chart-wide query) for the multi-highlight contributions of other lines.
 * The explicit seam calls (compileLineNotes, dropLine, onTempoChanged, updateMultiHint, rebuildEmissions) do the
 * same work eagerly.
 */

import type { LineId, NoteId } from '../../model/types';
import type { EngineCompileContext } from '../types';
import { MultiHintMap } from './multiHint';
import { buildLineRuntime, LineNotesRt, NOTE_HOLD, runtimeFreshness } from './runtime';
import { compareKey } from './sortKeys';

/** Sorted index of every real (non-fake) note of the chart, by (time, id). */
export interface RealNoteIndex {
  readonly n: number;
  readonly time: Float64Array;
  readonly id: Int32Array;
  /** Commit times (autoplay judgement: hit time, hold end for holds), sorted ascending. */
  readonly commit: Float64Array;
}

export interface NotesGlobal {
  tempoEpoch: number;
  readonly mh: MultiHintMap;
  /** Lines whose multi-highlight contribution must be re-applied. */
  readonly mhPending: Set<LineId>;
  /** NoteId -> line of the runtime that holds it. */
  readonly noteLine: Map<NoteId, LineId>;
  /** Last ctx.lineOrder() array seen (identity check for dropped lines) and its set. */
  lastLineOrder: readonly LineId[] | null;
  live: ReadonlySet<LineId>;
  /** Bumped by every runtime change (caches of derived global data compare against it). */
  revision: number;
  realIndex: RealNoteIndex | null;
  realIndexRevision: number;
  /** Opaque caches owned by other files of the module (emissions, frame scratch, HUD). */
  readonly caches: Map<string, unknown>;
}

const globals = new WeakMap<Map<LineId, unknown>, NotesGlobal>();

export function globalOf(ctx: EngineCompileContext): NotesGlobal {
  let g = globals.get(ctx.notesStore);
  if (g === undefined) {
    g = {
      tempoEpoch: 0,
      mh: new MultiHintMap(),
      mhPending: new Set(),
      noteLine: new Map(),
      lastLineOrder: null,
      live: new Set(),
      revision: 0,
      realIndex: null,
      realIndexRevision: -1,
      caches: new Map(),
    };
    globals.set(ctx.notesStore, g);
  }
  return g;
}

/** Typed cache slot owned by one file of the module. */
export function cacheOf<T>(g: NotesGlobal, key: string, make: () => T): T {
  let v = g.caches.get(key) as T | undefined;
  if (v === undefined) {
    v = make();
    g.caches.set(key, v);
  }
  return v;
}

function storedRt(ctx: EngineCompileContext, line: LineId): LineNotesRt | undefined {
  const v = ctx.notesStore.get(line);
  return v instanceof LineNotesRt ? v : undefined;
}

function install(ctx: EngineCompileContext, g: NotesGlobal, line: LineId, rt: LineNotesRt | null): void {
  const old = storedRt(ctx, line);
  if (old !== undefined) {
    for (const id of old.indexOf.keys()) {
      if (g.noteLine.get(id) === line) g.noteLine.delete(id);
    }
  }
  if (rt === null) ctx.notesStore.delete(line);
  else {
    ctx.notesStore.set(line, rt);
    for (const id of rt.indexOf.keys()) g.noteLine.set(id, line);
  }
  g.mhPending.add(line);
  g.revision++;
}

/** Builds (or rebuilds) the runtime of `line` now. */
export function compileLine(ctx: EngineCompileContext, line: LineId): LineNotesRt {
  const g = globalOf(ctx);
  const rt = buildLineRuntime(ctx, line, ctx.irLine(line), g.tempoEpoch);
  install(ctx, g, line, rt);
  return rt;
}

/** Forgets a line's runtime. */
export function dropLineRt(ctx: EngineCompileContext, line: LineId): void {
  install(ctx, globalOf(ctx), line, null);
}

/** Marks every note second stale (rebuilt lazily on the next access of each line). */
export function invalidateTempo(ctx: EngineCompileContext): void {
  const g = globalOf(ctx);
  g.tempoEpoch++;
  g.revision++;
}

/** The fresh runtime of `line` (rebuilt when stale or missing). */
export function lineRt(ctx: EngineCompileContext, line: LineId): LineNotesRt {
  const g = globalOf(ctx);
  const ir = ctx.irLine(line);
  const rt = storedRt(ctx, line);
  if (rt !== undefined) {
    const f = runtimeFreshness(rt, ir, ctx, g.tempoEpoch);
    if (f === 'fresh') return rt;
    if (f === 'maxTimeOnly') {
      rt.keys = { ...rt.keys, maxTime: ctx.maxTime };
      return rt;
    }
  }
  const next = buildLineRuntime(ctx, line, ir, g.tempoEpoch);
  install(ctx, g, line, next);
  return next;
}

/**
 * The runtime of `line` as last synced (no freshness check): for frame passes that already called syncAll in the
 * same call. Falls back to lineRt for a line the sync did not see.
 */
export function syncedRt(ctx: EngineCompileContext, line: LineId): LineNotesRt {
  return storedRt(ctx, line) ?? lineRt(ctx, line);
}

/**
 * Follows the line set: when `ctx.lineOrder()` is a new array, runtimes of lines that are gone are dropped (and
 * their multi-highlight contribution removed). O(1) while the order array is unchanged.
 */
export function refreshTopology(ctx: EngineCompileContext, g: NotesGlobal = globalOf(ctx)): NotesGlobal {
  const order = ctx.lineOrder();
  if (order === g.lastLineOrder) return g;
  const live = new Set(order);
  const dead: LineId[] = [];
  for (const key of ctx.notesStore.keys()) if (!live.has(key)) dead.push(key);
  for (const line of dead) install(ctx, g, line, null);
  for (const line of g.mh.lines()) if (!live.has(line)) g.mhPending.add(line);
  g.live = live;
  g.lastLineOrder = order;
  return g;
}

/** Brings every line of `ctx.lineOrder()` up to date, drops runtimes of deleted lines, applies pending MH. */
export function syncAll(ctx: EngineCompileContext): NotesGlobal {
  const g = refreshTopology(ctx);
  for (const line of ctx.lineOrder()) lineRt(ctx, line);
  applyMultiHint(ctx, g);
  return g;
}

/** Applies pending multi-highlight contributions (and those of `extra` lines). */
export function applyMultiHint(ctx: EngineCompileContext, g: NotesGlobal, extra?: ReadonlySet<LineId>): void {
  if (extra !== undefined) for (const line of extra) g.mhPending.add(line);
  if (g.mhPending.size === 0) return;
  for (const line of g.mhPending) g.mh.setLine(line, storedRt(ctx, line) ?? null);
  g.mhPending.clear();
}

/** Every fresh runtime in export order (after syncAll). */
export function runtimesInOrder(ctx: EngineCompileContext): LineNotesRt[] {
  const out: LineNotesRt[] = [];
  for (const line of ctx.lineOrder()) {
    const rt = storedRt(ctx, line);
    if (rt !== undefined) out.push(rt);
  }
  return out;
}

/**
 * Locates a note: its fresh runtime and sorted index. Only the note's own line is checked for freshness (per-note
 * queries stay O(1)); unknown ids, ids of deleted lines and notes that moved trigger a full sync.
 */
export function locateNote(ctx: EngineCompileContext, note: NoteId): { rt: LineNotesRt; i: number } | null {
  const g = refreshTopology(ctx);
  const find = (): { rt: LineNotesRt; i: number } | null => {
    const line = g.noteLine.get(note);
    if (line === undefined || !g.live.has(line)) return null;
    const rt = lineRt(ctx, line);
    const i = rt.indexOf.get(note);
    return i === undefined ? null : { rt, i };
  };
  let hit = find();
  if (hit === null) {
    syncAll(ctx);
    hit = find();
  }
  applyMultiHint(ctx, g);
  return hit;
}

/** Sorted index of real notes over every line (rebuilt when any runtime changed). */
export function realNoteIndex(ctx: EngineCompileContext): RealNoteIndex {
  const g = syncAll(ctx);
  if (g.realIndex !== null && g.realIndexRevision === g.revision) return g.realIndex;
  const times: number[] = [];
  const ids: number[] = [];
  const commits: number[] = [];
  for (const rt of runtimesInOrder(ctx)) {
    for (let i = 0; i < rt.n; i++) {
      if (rt.fake[i] === 1) continue;
      const t = rt.time[i] ?? 0;
      commits.push(rt.type[i] === NOTE_HOLD ? Math.max(t, rt.endTime[i] ?? t) : t);
      const id = rt.id[i] ?? -1;
      if (id < 0) continue;
      times.push(t);
      ids.push(id);
    }
  }
  const order = times.map((_, k) => k);
  order.sort((a, b) => compareKey(times[a] ?? 0, times[b] ?? 0) || (ids[a] ?? 0) - (ids[b] ?? 0));
  const idx: RealNoteIndex = {
    n: order.length,
    time: Float64Array.from(order, (k) => times[k] ?? 0),
    id: Int32Array.from(order, (k) => ids[k] ?? 0),
    commit: Float64Array.from(commits).sort(),
  };
  g.realIndex = idx;
  g.realIndexRevision = g.revision;
  return idx;
}
